import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { currentLocale } from '../../i18n';
import { API_URL } from '../../utils/api';
import './AdminEconomy.css';

/**
 * Chart colors, validated for colorblind separation and contrast against both
 * the dark (#0a0a0f) and light chart surfaces. Blue/orange rather than
 * green/red on purpose: burning points is healthy in an economy, so the pair
 * must not imply that outflow is a failure.
 * @type {{minted: string, burned: string, transfer: string}}
 */
const COLORS = {
  minted: '#3b82f6',
  burned: '#e06000',
  transfer: '#16a34a',
};

/**
 * Selectable window lengths for flows and the daily series.
 * @type {Array<number>}
 */
const WINDOWS = [7, 30, 90, 365];

/**
 * Maps a flow class from the API to its i18n label key and color.
 * @type {Object<string, {labelKey: string, color: string}>}
 */
const CLASS_META = {
  faucet: { labelKey: 'admin.econClassFaucet', color: COLORS.minted },
  sink: { labelKey: 'admin.econClassSink', color: COLORS.burned },
  transfer: { labelKey: 'admin.econClassTransfer', color: COLORS.transfer },
  adjust: { labelKey: 'admin.econClassAdjust', color: COLORS.transfer },
  unclassified: { labelKey: 'admin.econClassUnclassified', color: COLORS.burned },
};

/**
 * Formats a point amount with locale-aware thousands separators.
 * @param {number} value - The amount in points.
 * @returns {string} Formatted number, or "0" when the value is missing.
 */
function formatPoints(value) {
  if (value === null || value === undefined) return '0';
  return Number(value).toLocaleString(currentLocale());
}

/**
 * A single headline figure with a label and optional sub-line.
 * @param {Object} props
 * @param {string} props.label - What the figure measures.
 * @param {number|string} props.value - The figure itself.
 * @param {string} [props.hint] - Optional clarifying sub-line.
 * @param {string} [props.tone] - Optional tone class suffix (minted/burned).
 */
function StatTile({ label, value, hint, tone }) {
  return (
    <div className={`admin-economy-tile${tone ? ` admin-economy-tile-${tone}` : ''}`}>
      <span className="admin-economy-tile-label">{label}</span>
      <span className="admin-economy-tile-value">{value}</span>
      {hint && <span className="admin-economy-tile-hint">{hint}</span>}
    </div>
  );
}

/**
 * Admin view for the points economy: money supply, faucets vs sinks, the
 * daily mint/burn series, wallet concentration and the community pot.
 *
 * The taxonomy is the point of the view — transfers (stakes, payouts,
 * marketplace trades) net to zero and are shown apart from faucets and sinks
 * so inflation is visible instead of being hidden inside a net figure.
 */
export default function AdminEconomy() {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/api/predictions/admin/economy?days=${days}`,
        { headers: getAuthHeaders() },
      );
      if (res.status === 401 || res.status === 403) {
        setError(t('admin.noAccess'));
        setData(null);
        return;
      }
      if (!res.ok) {
        setError(t('admin.errorStatus', { status: res.status }));
        setData(null);
        return;
      }
      setData(await res.json());
    } catch (e) {
      setError(t('settings.networkError'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days, t, getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const classRows = useMemo(() => {
    if (!data) return [];
    const byClass = data.flows.window.by_class || {};
    return Object.entries(byClass).map(([klass, totals]) => ({
      klass,
      label: t(CLASS_META[klass]?.labelKey || 'admin.econClassUnclassified'),
      color: CLASS_META[klass]?.color || COLORS.burned,
      ...totals,
    }));
  }, [data, t]);

  const kindRows = useMemo(() => {
    if (!data) return [];
    // Kopia före sort — arrayen ligger i state och sort muterar.
    return [...(data.flows.window.by_kind || [])];
  }, [data]);

  if (loading) return <div className="admin-economy-loading">{t('live.loading')}</div>;
  if (error) return <div className="admin-economy-error">{error}</div>;
  if (!data) return null;

  const { supply, distribution, house_pot: housePot, series, warnings } = data;
  const circulating = supply.player_balances + supply.house_pot;

  return (
    <div className="admin-economy">
      <div className="admin-economy-header">
        <h2 className="admin-economy-title">{t('admin.econTitle')}</h2>
        <div className="admin-economy-windows">
          {WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              className={`admin-economy-window${w === days ? ' active' : ''}`}
              onClick={() => setDays(w)}
            >
              {t('admin.econDays', { count: w })}
            </button>
          ))}
        </div>
      </div>

      {warnings && warnings.length > 0 && (
        <div className="admin-economy-warning">
          {warnings.map((w) => (
            <p key={w}>{w}</p>
          ))}
        </div>
      )}

      <section className="admin-economy-section">
        <h3 className="admin-economy-subtitle">{t('admin.econSupplyTitle')}</h3>
        <p className="admin-economy-desc">{t('admin.econSupplyDesc')}</p>
        <div className="admin-economy-tiles">
          <StatTile
            label={t('admin.econSupplyTotal')}
            value={formatPoints(circulating)}
            hint={t('admin.econSupplyTotalHint')}
          />
          <StatTile
            label={t('admin.econMinted')}
            value={formatPoints(supply.minted_all_time)}
            hint={t('admin.econMintedHint')}
            tone="minted"
          />
          <StatTile
            label={t('admin.econBurned')}
            value={formatPoints(supply.burned_all_time)}
            hint={t('admin.econBurnedHint')}
            tone="burned"
          />
          <StatTile
            label={t('admin.econPot')}
            value={formatPoints(housePot.balance)}
            hint={t('admin.econPotHint', { pct: housePot.recycle_pct })}
          />
        </div>
      </section>

      <section className="admin-economy-section">
        <h3 className="admin-economy-subtitle">{t('admin.econSeriesTitle')}</h3>
        <p className="admin-economy-desc">{t('admin.econSeriesDesc')}</p>
        {series.length === 0 ? (
          <p className="admin-economy-empty">{t('admin.econNoFlows')}</p>
        ) : (
          <div className="admin-economy-chart">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={series} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeOpacity={0.12} vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickMargin={8} />
                <YAxis tick={{ fontSize: 11 }} width={56} />
                <Tooltip
                  formatter={(value) => formatPoints(value)}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="minted"
                  name={t('admin.econMinted')}
                  stroke={COLORS.minted}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="burned"
                  name={t('admin.econBurned')}
                  stroke={COLORS.burned}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      <section className="admin-economy-section">
        <h3 className="admin-economy-subtitle">{t('admin.econClassTitle')}</h3>
        <p className="admin-economy-desc">{t('admin.econClassDesc')}</p>
        <div className="admin-economy-classes">
          {classRows.map((row) => (
            <div key={row.klass} className="admin-economy-class">
              <span
                className="admin-economy-class-swatch"
                style={{ backgroundColor: row.color }}
                aria-hidden="true"
              />
              <span className="admin-economy-class-label">{row.label}</span>
              <span className="admin-economy-class-in">
                +{formatPoints(row.inflow)}
              </span>
              <span className="admin-economy-class-out">
                −{formatPoints(row.outflow)}
              </span>
              <span className="admin-economy-class-net">
                {row.net >= 0 ? '+' : '−'}
                {formatPoints(Math.abs(row.net))}
              </span>
            </div>
          ))}
        </div>

        <table className="admin-economy-table">
          <thead>
            <tr>
              <th>{t('admin.econColKind')}</th>
              <th>{t('admin.econColClass')}</th>
              <th>{t('admin.econColIn')}</th>
              <th>{t('admin.econColOut')}</th>
              <th>{t('admin.econColCount')}</th>
            </tr>
          </thead>
          <tbody>
            {kindRows.length === 0 && (
              <tr>
                <td colSpan={5} className="admin-economy-empty">
                  {t('admin.econNoFlows')}
                </td>
              </tr>
            )}
            {kindRows.map((row) => (
              <tr key={row.kind}>
                <td>{row.label}</td>
                <td>
                  {t(CLASS_META[row.class]?.labelKey || 'admin.econClassUnclassified')}
                </td>
                <td className="admin-economy-num">{formatPoints(row.inflow)}</td>
                <td className="admin-economy-num">{formatPoints(row.outflow)}</td>
                <td className="admin-economy-num">{row.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="admin-economy-section">
        <h3 className="admin-economy-subtitle">{t('admin.econDistTitle')}</h3>
        <p className="admin-economy-desc">
          {t('admin.econDistDesc', {
            wallets: distribution.wallets,
            pct: distribution.top10_share_pct,
          })}
        </p>
        <div className="admin-economy-tiles">
          <StatTile
            label={t('admin.econMedian')}
            value={formatPoints(distribution.median_balance)}
          />
          <StatTile
            label={t('admin.econMean')}
            value={formatPoints(distribution.mean_balance)}
          />
          <StatTile
            label={t('admin.econEmptyWallets')}
            value={formatPoints(distribution.zero_balance_wallets)}
            hint={t('admin.econEmptyWalletsHint')}
          />
        </div>
        {distribution.top_holders.length > 0 && (
          <div className="admin-economy-chart">
            <ResponsiveContainer width="100%" height={40 + distribution.top_holders.length * 28}>
              <BarChart
                data={distribution.top_holders}
                layout="vertical"
                margin={{ top: 4, right: 48, bottom: 4, left: 0 }}
              >
                <XAxis type="number" hide />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={140}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip
                  formatter={(value) => formatPoints(value)}
                  contentStyle={{ fontSize: 12 }}
                />
                <Bar
                  dataKey="balance"
                  name={t('admin.econBalance')}
                  fill={COLORS.minted}
                  radius={[0, 4, 4, 0]}
                  barSize={16}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>
    </div>
  );
}
